import * as THREE from 'three';
import type { Dog } from '../dog/Dog';

/**
 * Her house. At night, or when it rains, she takes herself in and lies down;
 * she comes out at dawn, when the rain stops, or when you call her.
 */
export class Home {
  readonly root = new THREE.Group();
  readonly door = new THREE.Vector3();
  readonly bed = new THREE.Vector3();
  onMessage?: (text: string) => void;
  private state: 'out' | 'going' | 'in' = 'out';
  private wantIn = false;
  private checkT = 0;

  constructor(
    scene: THREE.Object3D,
    private dog: Dog,
    position: THREE.Vector3,
    yaw: number,
  ) {
    const wall = new THREE.MeshStandardMaterial({ color: 0xc9b48f, roughness: 0.95 });
    const roofMat = new THREE.MeshStandardMaterial({ color: 0x7b5cc4, roughness: 0.8 });
    const dark = new THREE.MeshStandardMaterial({ color: 0x1e1a16, roughness: 1 });
    const s = dog.profile.size;
    const w = 1.1 * s;
    const h = 0.95 * s;
    const d = 1.2 * s;
    const body = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), wall);
    body.position.y = h / 2;
    body.castShadow = true;
    body.receiveShadow = true;
    // door opening on the front (+z)
    const doorway = new THREE.Mesh(new THREE.PlaneGeometry(w * 0.5, h * 0.72), dark);
    doorway.position.set(0, h * 0.38, d / 2 + 0.003);
    // roof: two slabs
    const slab = new THREE.BoxGeometry(w * 0.72, 0.05, d + 0.2);
    const r1 = new THREE.Mesh(slab, roofMat);
    r1.position.set(-w * 0.28, h + 0.22, 0);
    r1.rotation.z = 0.7;
    const r2 = new THREE.Mesh(slab, roofMat);
    r2.position.set(w * 0.28, h + 0.22, 0);
    r2.rotation.z = -0.7;
    for (const m of [r1, r2]) m.castShadow = true;
    // gable: a triangular prism that fills the attic under the two slabs
    const ridge = h + 0.22 + 0.232 * w;
    const g = new THREE.BufferGeometry();
    const hw = w / 2 - 0.01;
    const hd = d / 2;
    // prettier-ignore
    const verts = new Float32Array([
      // front triangle
      -hw, h, hd,   hw, h, hd,   0, ridge, hd,
      // back triangle
      hw, h, -hd,  -hw, h, -hd,  0, ridge, -hd,
      // left slope (two triangles)
      -hw, h, hd,   0, ridge, hd,   0, ridge, -hd,
      -hw, h, hd,   0, ridge, -hd,  -hw, h, -hd,
      // right slope
      hw, h, -hd,   0, ridge, -hd,  0, ridge, hd,
      hw, h, -hd,   0, ridge, hd,   hw, h, hd,
    ]);
    g.setAttribute('position', new THREE.BufferAttribute(verts, 3));
    g.computeVertexNormals();
    const gable = new THREE.Mesh(g, wall);
    gable.castShadow = true;
    this.root.add(gable);
    // name plate over the door
    const plate = new THREE.Mesh(new THREE.PlaneGeometry(w * 0.6, 0.14), new THREE.MeshBasicMaterial({ map: nameTexture(dog.profile.name) }));
    plate.position.set(0, h * 0.86, d / 2 + 0.004);
    this.root.add(body, doorway, r1, r2, plate);
    this.root.position.copy(position);
    this.root.rotation.y = yaw;
    this.root.name = 'house';
    this.root.userData.tapName = 'house';
    scene.add(this.root);
    this.root.updateMatrixWorld(true);
    this.door.set(0, 0, d / 2 + 0.55 * s).applyMatrix4(this.root.matrixWorld);
    this.bed.set(0, 0, -0.05 * s).applyMatrix4(this.root.matrixWorld);
  }

  get inside(): boolean {
    return this.state === 'in';
  }

  /** the world tells us whether it's a night or a rainy moment */
  update(dt: number, shelterWeather: boolean): void {
    this.checkT += dt;
    const dog = this.dog;
    // anything the player does with her cancels a trip home
    if (this.state !== 'out' && !dog.homing) {
      this.state = 'out';
    }
    if (this.checkT < 1) return;
    this.checkT = 0;
    this.wantIn = shelterWeather;
    switch (this.state) {
      case 'out':
        if (this.wantIn && dog.idle && dog.hunger < 0.85) {
          dog.goHome(this.door, this.bed);
          this.state = 'going';
          this.onMessage?.(shelterWeather ? `${dog.profile.name} is heading in` : '');
        }
        break;
      case 'going':
        if (dog.settledHome) this.state = 'in';
        break;
      case 'in':
        if (!this.wantIn) {
          dog.leaveHome(this.door);
          this.state = 'out';
        }
        break;
    }
  }
}

function nameTexture(text: string): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 256;
  c.height = 64;
  const g = c.getContext('2d')!;
  g.fillStyle = '#5a3d22';
  g.fillRect(0, 0, c.width, c.height);
  g.fillStyle = '#f6e7c8';
  g.font = 'bold 40px system-ui, sans-serif';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(text.toUpperCase(), c.width / 2, c.height / 2 + 2);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
